import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './App.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api');

function App() {
  const [shipments, setShipments] = useState([]);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [scanValue, setScanValue] = useState('');
  const [scanStatus, setScanStatus] = useState(null);
  const [scanMessage, setScanMessage] = useState('');
  const [activeView, setActiveView] = useState('scan');
  const [lists, setLists] = useState([]);
  const [totalUniqueItems, setTotalUniqueItems] = useState(0);
  const [aggregateStats, setAggregateStats] = useState(null);
  const scanInputRef = useRef(null);
  const [hubMobile, setHubMobile] = useState(false);

  useEffect(() => {
    fetchShipments();
    fetchLists();
    if (scanInputRef.current) {
      scanInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (window.parent === window) return undefined;
    document.documentElement.classList.add("in-hub");
    const query = window.matchMedia("(max-width: 860px)");
    const sync = () => {
      const mobile = query.matches;
      document.documentElement.classList.toggle("in-hub-mobile", mobile);
      setHubMobile(mobile);
    };
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    const refreshData = async () => {
      await fetchShipments();
      if (activeView === 'lists') {
        await fetchLists();
      }
      if (selectedShipment?.id) {
        const response = await axios.get(`${API_BASE_URL}/shipments/${selectedShipment.id}`);
        setSelectedShipment(response.data);
      }
    };

    const intervalId = setInterval(refreshData, 5000);
    return () => clearInterval(intervalId);
  }, [activeView, selectedShipment?.id]);

  useEffect(() => {
    if (activeView === 'scan' && scanInputRef.current) {
      setTimeout(() => scanInputRef.current?.focus(), 100);
    }
  }, [activeView]);

  const fetchShipments = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/shipments`);
      setShipments(response.data.shipments || response.data);
      setAggregateStats(response.data.aggregateStats || null);
      if (!selectedShipment && (response.data.shipments || response.data).length > 0) {
        setSelectedShipment((response.data.shipments || response.data)[0]);
      }
    } catch (error) {
      console.error('Error fetching shipments:', error);
    }
  };

  const fetchLists = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/lists`);
      setLists(response.data.lists || response.data);
      setTotalUniqueItems(response.data.totalUniqueItems || 0);
    } catch (error) {
      console.error('Error fetching lists:', error);
    }
  };

  const handleScan = async (e) => {
    e.preventDefault();
    
    if (!scanValue.trim()) return;
    
    if (!selectedShipment) {
      setScanStatus('error');
      setScanMessage('Error - Select Shipment');
      setTimeout(() => {
        setScanStatus(null);
        setScanMessage('');
      }, 2000);
      return;
    }
    
    if (selectedShipment.closed) {
      setScanStatus('error');
      setScanMessage('Batch is closed');
      setTimeout(() => {
        setScanStatus(null);
        setScanMessage('');
      }, 2000);
      return;
    }
    
    try {
      const response = await axios.post(`${API_BASE_URL}/scan`, {
        scanValue: scanValue.trim(),
        shipmentId: selectedShipment.id
      });
      
      // Always refresh shipments and selected shipment after scan attempt
      await fetchShipments();
      if (selectedShipment) {
        const response2 = await axios.get(`${API_BASE_URL}/shipments/${selectedShipment.id}`);
        setSelectedShipment(response2.data);
      }
      
      if (response.data.success) {
        setScanStatus('success');
        setScanMessage('Success');
      } else {
        // Check if it's a duplicate to show orange instead of red
        if (response.data.rejectType === 'duplicate') {
          setScanStatus('duplicate');
          setScanMessage('Duplicate');
        } else {
          setScanStatus('reject');
          setScanMessage(response.data.message || 'Reject');
        }
      }
      
      setScanValue('');
      setTimeout(() => {
        setScanStatus(null);
        setScanMessage('');
        if (scanInputRef.current) {
          scanInputRef.current.focus();
        }
      }, 2000);
      
    } catch (error) {
      // Check if it's a duplicate error
      if (error.response?.data?.rejectType === 'duplicate') {
        setScanStatus('duplicate');
        setScanMessage('Duplicate');
      } else {
        setScanStatus('reject');
        setScanMessage(error.response?.data?.message || 'Reject');
      }
      setScanValue('');
      setTimeout(() => {
        setScanStatus(null);
        setScanMessage('');
        if (scanInputRef.current) {
          scanInputRef.current.focus();
        }
      }, 2000);
    }
  };

  const createShipment = async (warehouse) => {
    if (!warehouse || (warehouse !== 'NJ' && warehouse !== 'TX')) {
      console.error('Invalid warehouse:', warehouse);
      return;
    }
    
    try {
      const response = await axios.post(`${API_BASE_URL}/shipments`, { warehouse });
      await fetchShipments();
      setSelectedShipment(response.data);
      setActiveView('scan');
    } catch (error) {
      console.error('Error creating shipment:', error);
      alert('Error creating batch: ' + (error.response?.data?.error || error.message));
    }
  };

  const closeShipment = async (shipmentId) => {
    if (!window.confirm('Are you sure you want to close this batch?')) return;

    const palletsInput = window.prompt('How many pallets are in this batch?', '0');
    if (palletsInput === null) return;
    const palletsCount = Number.parseInt(palletsInput, 10);
    if (!Number.isInteger(palletsCount) || palletsCount < 0) {
      alert('Please enter a non-negative whole number for pallets.');
      return;
    }
    
    try {
      const response = await axios.post(`${API_BASE_URL}/shipments/${shipmentId}/close`, {
        palletsCount
      });
      // Refresh shipments list
      await fetchShipments();
      
      if (selectedShipment?.id === shipmentId) {
        setSelectedShipment(null);
      }
    } catch (error) {
      console.error('Error closing shipment:', error);
      // Check if the batch was actually closed (sometimes network errors occur after success)
      if (error.response?.status >= 400) {
        alert('Error closing batch: ' + (error.response?.data?.error || error.message));
      } else {
        // If it's a network error but batch might be closed, refresh and check
        await fetchShipments();
      }
    }
  };

  const reopenShipment = async (shipmentId) => {
    try {
      await axios.post(`${API_BASE_URL}/shipments/${shipmentId}/reopen`);
      await fetchShipments();
      const response = await axios.get(`${API_BASE_URL}/shipments/${shipmentId}`);
      setSelectedShipment(response.data);
    } catch (error) {
      console.error('Error reopening shipment:', error);
      alert('Error reopening batch');
    }
  };

  const deleteShipment = async (shipmentId) => {
    if (!window.confirm('Are you sure you want to delete this batch?')) return;
    
    try {
      await axios.delete(`${API_BASE_URL}/shipments/${shipmentId}`);
      await fetchShipments();
      if (selectedShipment?.id === shipmentId) {
        setSelectedShipment(shipments.length > 1 ? shipments.find(s => s.id !== shipmentId) : null);
      }
    } catch (error) {
      console.error('Error deleting shipment:', error);
      alert('Error deleting batch');
    }
  };

  const downloadShipment = async (shipmentId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/shipments/${shipmentId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const shipment = shipments.find(s => s.id === shipmentId);
      link.setAttribute('download', `batch-${shipment?.name || shipmentId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading shipment:', error);
      alert('Error downloading batch');
    }
  };

  const printPalletLabel = (shipment) => {
    if (!shipment || !shipment.closed) {
      alert('Batch must be closed to print pallet label');
      return;
    }

    const stats = shipment.stats || {
      totalScans: 0,
      accepted: 0,
      rejected: 0,
      duplicates: 0
    };

    // Create print window content
    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Pallet Label - ${shipment.name}</title>
          <style>
            @page {
              size: 4in 6in;
              margin: 0.25in;
            }
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: Arial, sans-serif;
              width: 4in;
              height: 6in;
              padding: 0.25in;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
            }
            .label-header {
              text-align: center;
              border-bottom: 3px solid #000;
              padding: 0.15in 0.1in;
              margin-bottom: 0.2in;
              box-sizing: border-box;
            }
            .label-title {
              font-size: 0.35in;
              font-weight: bold;
              margin-bottom: 0.1in;
            }
            .batch-name {
              font-size: 0.5in;
              font-weight: bold;
              color: #000;
              margin: 0;
              padding: 0;
              word-wrap: break-word;
              overflow-wrap: break-word;
              max-width: 100%;
            }
            .label-section {
              margin: 0.15in 0;
            }
            .label-section-title {
              font-size: 0.2in;
              font-weight: bold;
              border-bottom: 1px solid #000;
              padding-bottom: 0.05in;
              margin-bottom: 0.1in;
            }
            .label-info {
              display: flex;
              justify-content: space-between;
              margin: 0.08in 0;
              font-size: 0.18in;
            }
            .label-info-label {
              font-weight: bold;
            }
            .label-info-value {
              text-align: right;
            }
            .label-stats {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 0.1in;
              margin-top: 0.15in;
            }
            .stat-item {
              padding: 0.1in;
              background: #f5f5f5;
              border: 1px solid #ccc;
              text-align: center;
            }
            .stat-value {
              font-size: 0.3in;
              font-weight: bold;
              margin-bottom: 0.05in;
            }
            .stat-label {
              font-size: 0.14in;
              color: #666;
            }
            .label-footer {
              text-align: center;
              font-size: 0.16in;
              color: #666;
              margin-top: auto;
              padding-top: 0.15in;
              border-top: 1px solid #ccc;
            }
            @media print {
              body {
                margin: 0;
                padding: 0.25in;
              }
            }
          </style>
        </head>
        <body>
          <div class="label-header">
            <div class="batch-name">${shipment.name || 'N/A'}</div>
          </div>
          
          <div class="label-section">
            <div class="label-section-title">BATCH INFORMATION</div>
            <div class="label-info">
              <span class="label-info-label">Warehouse:</span>
              <span class="label-info-value">${shipment.warehouse || 'N/A'}</span>
            </div>
            <div class="label-info">
              <span class="label-info-label">Items Added:</span>
              <span class="label-info-value">${shipment.itemCount || stats.accepted || 0}</span>
            </div>
            <div class="label-info">
              <span class="label-info-label">Pallets:</span>
              <span class="label-info-value">${shipment.palletsCount ?? 0}</span>
            </div>
            <div class="label-info">
              <span class="label-info-label">Created:</span>
              <span class="label-info-value">${new Date(shipment.createdAt).toLocaleDateString()}</span>
            </div>
            <div class="label-info">
              <span class="label-info-label">Closed:</span>
              <span class="label-info-value">${shipment.closedAt ? new Date(shipment.closedAt).toLocaleDateString() : 'N/A'}</span>
            </div>
          </div>

          <div class="label-footer">
            Printed: ${new Date().toLocaleString()}
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Wait for content to load, then print
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const listName = prompt('Enter list name (optional):') || `List ${new Date().toLocaleString()}`;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', listName);
    
    try {
      const response = await axios.post(`${API_BASE_URL}/lists/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await fetchLists();
      if (response.data.duplicatesRemoved > 0) {
        alert(`List uploaded successfully!\n${response.data.message}\n${response.data.duplicatesRemoved} duplicates were removed.`);
      } else {
        alert('List uploaded successfully!');
      }
      e.target.value = '';
    } catch (error) {
      console.error('Error uploading file:', error);
      if (error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert('Error uploading list');
      }
    }
  };

  const deleteList = async (listId) => {
    if (!window.confirm('Are you sure you want to delete this list?')) return;
    
    try {
      await axios.delete(`${API_BASE_URL}/lists/${listId}`);
      await fetchLists();
    } catch (error) {
      console.error('Error deleting list:', error);
      alert('Error deleting list');
    }
  };

  const clearAllLists = async () => {
    const confirmMessage = 'WARNING: This will delete ALL lists, ALL batches, and ALL data. This action cannot be undone!\n\nAre you absolutely sure?';
    if (!window.confirm(confirmMessage)) return;
    
    const doubleConfirm = window.confirm('Final confirmation: Delete EVERYTHING?');
    if (!doubleConfirm) return;
    
    try {
      await axios.post(`${API_BASE_URL}/lists/clear-all`);
      await fetchLists();
      await fetchShipments();
      setSelectedShipment(null);
      alert('All data has been cleared successfully.');
    } catch (error) {
      console.error('Error clearing all data:', error);
      alert('Error clearing data');
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <div className="header-brand">
            {hubMobile ? (
              <button
                type="button"
                className="hub-menu"
                aria-label="Open menu"
                onClick={() => window.parent.postMessage({ type: "zbk-hub-menu" }, "*")}
              >
                <span></span><span></span><span></span>
              </button>
            ) : null}
            <h1>ZBK Batch Tracker</h1>
          </div>
          <nav className="header-nav">
            <button 
              className={activeView === 'scan' ? 'active' : ''}
              onClick={() => setActiveView('scan')}
            >
              Scanner
            </button>
            <button 
              className={activeView === 'batches' ? 'active' : ''}
              onClick={() => setActiveView('batches')}
            >
              Batches
            </button>
            <button 
              className={activeView === 'lists' ? 'active' : ''}
              onClick={() => setActiveView('lists')}
            >
              Lists
            </button>
          </nav>
        </div>
      </header>

      <div className="main-container">
        {/* Scanner View */}
        {activeView === 'scan' && (
          <div className="scanner-view">
            <div className="scanner-container">
              <form onSubmit={handleScan} className="scan-form">
                <input
                  ref={scanInputRef}
                  type="text"
                  value={scanValue}
                  onChange={(e) => setScanValue(e.target.value)}
                  placeholder="Scan ASIN/ISBN..."
                  className={`scan-input ${scanStatus ? scanStatus : ''}`}
                  autoFocus
                />
                <button type="submit" className="scan-submit">Scan</button>
              </form>

              <div className="scan-new-batch-buttons">
                <button
                  className="btn btn-new-batch btn-warehouse-nj"
                  onClick={() => createShipment('NJ')}
                >
                  <strong>New Batch - New Jersey</strong>
                </button>
                <button
                  className="btn btn-new-batch btn-warehouse-tx"
                  onClick={() => createShipment('TX')}
                >
                  <strong>New Batch - Texas</strong>
                </button>
              </div>
              
              {scanStatus && (
                <div className={`scan-feedback ${scanStatus}`}>
                  {scanMessage}
                </div>
              )}
            </div>

            <div className="current-shipment">
              {selectedShipment && !selectedShipment.closed ? (
                <>
                  <div className="shipment-label">Current Batch:</div>
                  <div className="shipment-name-large">
                    {selectedShipment.name}
                    {selectedShipment.closed && (
                      <span className="closed-badge">CLOSED</span>
                    )}
                  </div>
                  <div className="shipment-stats">
                    {selectedShipment.items?.length || selectedShipment.itemCount || 0} Items Added
                  </div>
                  {!selectedShipment.closed && (
                    <button 
                      className="btn btn-danger close-batch-btn"
                      onClick={() => closeShipment(selectedShipment.id)}
                    >
                      Close Batch
                    </button>
                  )}
                  {selectedShipment.closed && (
                    <button 
                      className="btn btn-primary close-batch-btn"
                      onClick={() => reopenShipment(selectedShipment.id)}
                    >
                      Reopen Batch
                    </button>
                  )}
                </>
              ) : (
                <div className="shipment-error">
                  Open New Shipment/Batch
                </div>
              )}
            </div>

            {/* Batch Selection */}
            <div className="batch-selection-panel">
              <h3>Select Batch</h3>
              <div className="batch-list">
                {shipments.filter(s => !s.closed).map(shipment => (
                  <div
                    key={shipment.id}
                    className={`batch-item ${selectedShipment?.id === shipment.id ? 'active' : ''}`}
                    onClick={async () => {
                      // Load full shipment data with items when selecting
                      const response = await axios.get(`${API_BASE_URL}/shipments/${shipment.id}`);
                      setSelectedShipment(response.data);
                    }}
                  >
                    <div className="batch-name">{shipment.name}</div>
                    <div className="batch-count">{shipment.itemCount || shipment.items?.length || 0} items</div>
                  </div>
                ))}
                {shipments.filter(s => s.closed).length > 0 && (
                  <div className="closed-batches-section">
                    <div className="closed-batches-label">Closed Batches</div>
                    {shipments.filter(s => s.closed).map(shipment => (
                      <div
                        key={shipment.id}
                        className={`batch-item closed ${selectedShipment?.id === shipment.id ? 'active' : ''}`}
                        onClick={async () => {
                          // Load full shipment data with items when selecting
                          const response = await axios.get(`${API_BASE_URL}/shipments/${shipment.id}`);
                          setSelectedShipment(response.data);
                        }}
                      >
                        <div className="batch-name">{shipment.name}</div>
                        <div className="batch-count">{shipment.itemCount || shipment.items?.length || 0} items</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {selectedShipment && (
              <div className="scan-statistics">
                <h3>Scan Statistics</h3>
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-value">
                      {selectedShipment.stats?.totalScans || 0}
                    </div>
                    <div className="stat-label">Total Scans</div>
                  </div>
                  <div className="stat-card success">
                    <div className="stat-value">
                      {selectedShipment.stats?.accepted || 0}
                    </div>
                    <div className="stat-label">Accepted</div>
                    <div className="stat-percentage">
                      {selectedShipment.stats?.totalScans > 0 
                        ? Math.round((selectedShipment.stats.accepted / selectedShipment.stats.totalScans) * 100)
                        : 0}%
                    </div>
                  </div>
                  <div className="stat-card reject">
                    <div className="stat-value">
                      {selectedShipment.stats?.rejected || 0}
                    </div>
                    <div className="stat-label">Rejected</div>
                    <div className="stat-percentage">
                      {selectedShipment.stats?.totalScans > 0 
                        ? Math.round((selectedShipment.stats.rejected / selectedShipment.stats.totalScans) * 100)
                        : 0}%
                    </div>
                  </div>
                  <div className="stat-card duplicate">
                    <div className="stat-value">
                      {selectedShipment.stats?.duplicates || 0}
                    </div>
                    <div className="stat-label">Duplicates</div>
                    <div className="stat-percentage">
                      {selectedShipment.stats?.totalScans > 0 
                        ? Math.round((selectedShipment.stats.duplicates / selectedShipment.stats.totalScans) * 100)
                        : 0}%
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Batches View */}
        {activeView === 'batches' && (
          <div className="batches-view">
            <div className="view-header">
              <h2>Batches</h2>
              <div className="warehouse-buttons">
                <button className="btn btn-warehouse-nj" onClick={() => createShipment('NJ')}>
                  <strong>+ New Batch NJ</strong>
                </button>
                <button className="btn btn-warehouse-tx" onClick={() => createShipment('TX')}>
                  <strong>+ New Batch TX</strong>
                </button>
              </div>
            </div>

            {aggregateStats && (
              <div className="aggregate-stats-panel">
                <h3>Total Statistics (All Batches)</h3>
                <div className="aggregate-stats-grid">
                  <div className="aggregate-stat-card">
                    <div className="aggregate-stat-value">{aggregateStats.totalBatches}</div>
                    <div className="aggregate-stat-label">Total Batches</div>
                  </div>
                  <div className="aggregate-stat-card">
                    <div className="aggregate-stat-value">{aggregateStats.totalScans}</div>
                    <div className="aggregate-stat-label">Total Scans</div>
                  </div>
                  <div className="aggregate-stat-card success">
                    <div className="aggregate-stat-value">{aggregateStats.totalAccepted}</div>
                    <div className="aggregate-stat-label">Accepted</div>
                    <div className="aggregate-stat-percentage">
                      {aggregateStats.totalScans > 0 
                        ? Math.round((aggregateStats.totalAccepted / aggregateStats.totalScans) * 100)
                        : 0}%
                    </div>
                  </div>
                  <div className="aggregate-stat-card reject">
                    <div className="aggregate-stat-value">{aggregateStats.totalRejected}</div>
                    <div className="aggregate-stat-label">Rejected</div>
                    <div className="aggregate-stat-percentage">
                      {aggregateStats.totalScans > 0 
                        ? Math.round((aggregateStats.totalRejected / aggregateStats.totalScans) * 100)
                        : 0}%
                    </div>
                  </div>
                  <div className="aggregate-stat-card duplicate">
                    <div className="aggregate-stat-value">{aggregateStats.totalDuplicates}</div>
                    <div className="aggregate-stat-label">Duplicates</div>
                    <div className="aggregate-stat-percentage">
                      {aggregateStats.totalScans > 0 
                        ? Math.round((aggregateStats.totalDuplicates / aggregateStats.totalScans) * 100)
                        : 0}%
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="batches-grid">
              {shipments.map(shipment => {
                const stats = shipment.stats || {
                  totalScans: 0,
                  accepted: 0,
                  rejected: 0,
                  duplicates: 0
                };
                const acceptRate = stats.totalScans > 0 ? Math.round((stats.accepted / stats.totalScans) * 100) : 0;
                const rejectRate = stats.totalScans > 0 ? Math.round((stats.rejected / stats.totalScans) * 100) : 0;
                
                return (
                  <div key={shipment.id} className="batch-card">
                    <div className="batch-card-header">
                      <h3>{shipment.name}</h3>
                      {shipment.closed && (
                        <span className="closed-badge-small">CLOSED</span>
                      )}
                    </div>
                    
                    <div className="batch-card-stats">
                      <div className="batch-stat-item">
                        <div className="batch-stat-value">{stats.totalScans}</div>
                        <div className="batch-stat-label">Total Scans</div>
                      </div>
                      <div className="batch-stat-item success">
                        <div className="batch-stat-value">{stats.accepted}</div>
                        <div className="batch-stat-label">Accepted</div>
                        <div className="batch-stat-percentage">{acceptRate}%</div>
                      </div>
                      <div className="batch-stat-item reject">
                        <div className="batch-stat-value">{stats.rejected}</div>
                        <div className="batch-stat-label">Rejected</div>
                        <div className="batch-stat-percentage">{rejectRate}%</div>
                      </div>
                      <div className="batch-stat-item duplicate">
                        <div className="batch-stat-value">{stats.duplicates}</div>
                        <div className="batch-stat-label">Duplicates</div>
                      </div>
                    </div>
                    
                    <div className="batch-card-meta">
                      <div className="batch-meta-item">
                        <span className="meta-label">Created:</span>
                        <span className="meta-value">
                          {new Date(shipment.createdAt).toLocaleString()}
                        </span>
                      </div>
                      <div className="batch-meta-item">
                        <span className="meta-label">Pallets:</span>
                        <span className="meta-value">
                          {shipment.palletsCount ?? 0}
                        </span>
                      </div>
                      {shipment.closed && shipment.closedAt && (
                        <div className="batch-meta-item">
                          <span className="meta-label">Closed:</span>
                          <span className="meta-value">
                            {new Date(shipment.closedAt).toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <div className="batch-card-actions">
                      {!shipment.closed && (
                        <button
                          className="btn btn-small"
                          onClick={() => {
                            setSelectedShipment(shipment);
                            setActiveView('scan');
                          }}
                        >
                          Select
                        </button>
                      )}
                      <button
                        className="btn btn-small"
                        onClick={() => downloadShipment(shipment.id)}
                      >
                        Download
                      </button>
                      {shipment.closed ? (
                        <>
                          <button
                            className="btn btn-small"
                            onClick={() => printPalletLabel(shipment)}
                          >
                            🖨️ Print Label
                          </button>
                          <button
                            className="btn btn-small"
                            onClick={() => reopenShipment(shipment.id)}
                          >
                            Reopen
                          </button>
                        </>
                      ) : (
                        <button
                          className="btn btn-small btn-danger"
                          onClick={() => closeShipment(shipment.id)}
                        >
                          Close
                        </button>
                      )}
                      <button
                        className="btn btn-small btn-danger"
                        onClick={() => deleteShipment(shipment.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {shipments.length === 0 && (
              <div className="empty-state">
                <p>No batches yet. Create your first batch to get started.</p>
              </div>
            )}
          </div>
        )}

        {/* Lists View */}
        {activeView === 'lists' && (
          <div className="lists-view">
            <div className="view-header">
              <h2>Uploaded Lists</h2>
              <div>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="file-input"
                  id="list-upload"
                />
                <label htmlFor="list-upload" className="btn btn-primary">
                  Upload List (CSV)
                </label>
              </div>
            </div>

            <div className="upload-info">
              <p className="help-text">CSV format: ASIN, ISBN, Title</p>
              <div className="total-unique-items">
                <span className="total-label">Total Unique Items Across All Lists:</span>
                <span className="total-value">{totalUniqueItems.toLocaleString()}</span>
              </div>
            </div>

            <div className="lists-grid">
              {lists.map(list => (
                <div key={list.id} className="list-card">
                  <div className="list-card-header">
                    <h3>{list.name}</h3>
                    <button
                      className="btn btn-small btn-danger"
                      onClick={() => deleteList(list.id)}
                    >
                      Delete
                    </button>
                  </div>
                  <div className="list-card-content">
                    <div className="list-stat">
                      <span className="stat-label">Items:</span>
                      <span className="stat-value">{list.itemCount || list.items?.length || 0}</span>
                    </div>
                    <div className="list-date">
                      Uploaded: {new Date(list.uploadedAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {lists.length === 0 && (
              <div className="empty-state">
                <p>No lists uploaded. Upload a CSV list to get started.</p>
              </div>
            )}

            {lists.length > 0 && (
              <div className="clear-all-section">
                <button 
                  className="btn btn-danger btn-small"
                  onClick={clearAllLists}
                >
                  🗑️ Clear All
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
